import React, { useState } from "react";
import { Alert } from "@mui/material";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { useNotification } from "@context/NotificationContext";
import { useAddToWaitingListBayHireRequest } from "@features/bay-hire/hooks/useBayHireMutations";
import { getApiErrorDetail } from "@features/bay-hire/utils/getApiErrorDetail";
import { useTranslation } from "react-i18next";

interface AddToWaitingListDialogProps {
  open: boolean;
  requestId: string;
  /** Falls back to `requestId` for the (should not happen) case where it is somehow absent. */
  requestNo?: number | null;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

/**
 * DFT-0044 (FE-61, AC39 decision): `05_Port_License_details-18.png` (PoF Visuals_Bay Hire 2) shows
 * a fieldless centered "Are you sure?" confirm, not a side drawer with Priority/Reason inputs.
 *
 * Rebuilt on the shared GenericDialog rather than SideDrawer + react-hook-form - there is no form
 * here any more. Confirming calls the mutation with an EMPTY request body: `priority`/`reason` were
 * already optional server-side (the backend defaults priority to the next available manual-order
 * slot and reason to "ADDED_TO_WAITING_LIST" when omitted - BayHireRequestService.java:991-996),
 * so removing the fields costs Operations only the ability to manually re-order the waiting list,
 * not the feature itself; ordering degrades to deterministic FCFS. No backend change is needed.
 */
const AddToWaitingListDialog: React.FC<AddToWaitingListDialogProps> = ({
  open,
  requestId,
  requestNo,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const addToWaitingListMutation = useAddToWaitingListBayHireRequest();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleClose = () => {
    if (addToWaitingListMutation.isPending) return;
    setSubmitError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitError(null);
    try {
      await addToWaitingListMutation.mutateAsync({ id: requestId, request: {} });
      // DFT-0044 FE-23: the baseline's success toast on the Operations Ongoing list
      // (06_Port_licenses_grid-2.png). The mockup ships a literal "[ID]" placeholder, which is a
      // recorded copy defect (Q19) - we resolve it to the request reference number per D52, never
      // the UUID. Same treatment FE-79 gave the confirm dialog's copy.
      notify({
        title: t("bay-hire.add-to-waiting-list-success", {
          requestNo: requestNo ?? requestId,
        }),
        type: "success",
      });
      await onSuccess();
    } catch (error) {
      // DFT-0077 (AD-5/AC16): this is one of the signal-based Ops decisions - a domain-side
      // rejection may never reach here as an HTTP error at all, so the fallback below is what
      // most users actually see for this flow. Not a gap: getApiErrorDetail still surfaces the
      // synchronous case (e.g. no active claimed task) when one IS returned.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.add-to-waiting-list-submit-error")));
    }
  };

  return (
    <GenericDialog
      open={open}
      testId="add-to-waiting-list-dialog"
      title={t("bay-hire.add-to-waiting-list-confirm-title")}
      cancelButtonText={t("common.cancel", { defaultValue: "Cancel" })}
      confirmButtonText={t("common.yes", { defaultValue: "Yes" })}
      handleCloseDialog={handleClose}
      confirmAction={() => void handleConfirm()}
      isSubmitting={addToWaitingListMutation.isPending}
      elementContent={
        <div className="flex flex-col gap-2">
          {submitError && (
            <Alert severity="error" data-testid="add-to-waiting-list-submit-error">
              {submitError}
            </Alert>
          )}
          <span data-testid="add-to-waiting-list-confirm-message">
            {t("bay-hire.add-to-waiting-list-confirm-message", {
              requestNo: requestNo ?? requestId,
            })}
          </span>
        </div>
      }
    />
  );
};

export default AddToWaitingListDialog;
